// React-Redux imports
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { CSSTransition } from "react-transition-group";
import { RootState } from "app/store";
import {
  setHeaderAction,
  setHeaderColorMode,
  setHeaderWidthMode,
  setIsSaveDialogOpen,
} from "features/header/headerSlice";

// Constants
import { SECTIONS, Sections, SectionName } from "./constants/sections";

// styled-components imports
import StyledContainer from "./styled-components/StyledContainer";
import StyledContent from "./styled-components/StyledContent";
import StyledNavigationGroup from "./styled-components/StyledNavigationGroup";
import StyledNavigationButton from "./styled-components/StyledNavigationButton";

// Common imports
import Stepper, { StepperProps } from "common/components/Stepper";
import ArrowRight from "common/svg/ArrowRight";
import ArrowLeft from "common/svg/ArrowLeft";
import StepperContainer from "common/components/styled-components/StepperContainer";
import Wrapper from "common/components/styled-components/Wrapper";
import {
  getInitialSteps,
  getProgressSectionBack,
  getProgressSectionNext,
  getProgressStep,
} from "common/utils/stepper";

import Section from "./Section";
import SaveDialog from "./saveDialog/SaveDialog";
import { trackCalculationEnds } from "features/cookie-banner/gtm-functions";

interface NavigationState {
  step: number;
  section: SectionName;
}

function Calculator() {
  const dispatch = useDispatch();
  const history = useHistory();
  const isSaveDialogOpen = useSelector(
    (state: RootState) => state.header.save.isDialogOpen
  );

  const recycle = useSelector(
    (state: RootState) => state.calculator.householdHabits.recycle
  );

  useEffect(() => {
    dispatch(setHeaderWidthMode("full"));
    dispatch(setHeaderColorMode("unicolor"));
    dispatch(setHeaderAction("save"));
    window.scrollTo(0, 0);
  }, []);

  const [animate, setAnimate] = useState<boolean>(true);
  const [stepperData, setStepperData] = useState<StepperProps>({
    steps: getInitialSteps<Sections>(SECTIONS),
    currentStep: 0,
  });

  const [navigation, setNavigation] = useState<NavigationState>({
    step: 1,
    section: "residence",
  });

  const { step, section } = navigation;

  function next() {
    const currentSection = SECTIONS[section];
    if (
      currentSection.order === Object.keys(SECTIONS).length - 1 &&
      step === currentSection.steps - 1 &&
      !recycle
    ) {
      trackCalculationEnds();
      history.push("/calculator/result");
    } else if (currentSection.steps >= step + 1) {
      setAnimate(true);
      setNavigation({ ...navigation, step: step + 1 });
      setStepperData(getProgressStep(stepperData, currentSection, step + 1));
    } else if (currentSection.order === Object.keys(SECTIONS).length - 1) {
      trackCalculationEnds();
      history.push("/calculator/result");
    } else {
      let sectionName: SectionName;
      for (sectionName in SECTIONS) {
        if (SECTIONS[sectionName].order === currentSection.order + 1) {
          setAnimate(true);
          setNavigation({ step: 1, section: sectionName });

          setStepperData(
            getProgressSectionNext(
              stepperData,
              currentSection,
              SECTIONS[sectionName]
            )
          );
          break;
        }
      }
    }
  }

  function back() {
    const currentSection = SECTIONS[section];
    if (step - 1 > 0) {
      setAnimate(true);
      setNavigation({ ...navigation, step: step - 1 });
      setStepperData(getProgressStep(stepperData, currentSection, step - 1));
    } else {
      let sectionName: SectionName;
      for (sectionName in SECTIONS) {
        if (SECTIONS[sectionName].order === currentSection.order - 1) {
          setAnimate(true);
          setNavigation({
            step: SECTIONS[sectionName].steps,
            section: sectionName,
          });

          setStepperData(
            getProgressSectionBack(
              stepperData,
              currentSection,
              SECTIONS[sectionName]
            )
          );
          break;
        }
      }
    }
  }

  function handleSaveDialog(action: "continue" | "save") {
    switch (action) {
      case "continue":
        return () => {
          dispatch(setIsSaveDialogOpen(false));
        };

      case "save":
        return () => {
          history.push("/calculator/result");
        };
    }
  }

  return (
    <Wrapper>
      {isSaveDialogOpen && (
        <SaveDialog
          onSave={handleSaveDialog("save")}
          onContinue={handleSaveDialog("continue")}
        />
      )}
      <StepperContainer>
        <Stepper {...stepperData} />
      </StepperContainer>
      <StyledContainer>
        <CSSTransition
          in={animate}
          classNames="content"
          timeout={200}
          onEntered={() => setAnimate(false)}
          appear
        >
          <StyledContent>
            <Section section={section} step={step} />
          </StyledContent>
        </CSSTransition>
        <StyledNavigationGroup>
          {!(section === "residence" && step === 1) && (
            <StyledNavigationButton onClick={back}>
              <ArrowLeft />
              <span>Atrás</span>
            </StyledNavigationButton>
          )}
          <StyledNavigationButton onClick={next}>
            <span>Siguiente</span>
            <ArrowRight />
          </StyledNavigationButton>
        </StyledNavigationGroup>
      </StyledContainer>
    </Wrapper>
  );
}

export default Calculator;
